import { useActiveRightStackJid, useIsOnline } from '@/app/store';
import { updateMediaStatus } from '@/features/chats/model/conversation/reducer';
import { handleMediaAction } from '@/features/chats/services/messageQueueManager';
import { DOWNLOAD_STATUS, mediaStatusConstants, UPLOAD_STATUS } from '@/shared/constants';
import { formatBytes, getUserIdFromJid, logger } from '@/shared/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const UPLOADING_STATUSES = new Set([UPLOAD_STATUS.PENDING, UPLOAD_STATUS.IN_PROGRESS, UPLOAD_STATUS.QUEUED]);
const DOWNLOADABLE_STATUSES = new Set([DOWNLOAD_STATUS.NOT_STARTED, DOWNLOAD_STATUS.FAILED]);
const PERSISTENT_DOWNLOAD_TYPES = new Set(['file']);
const MEDIA_ACTION_MAP = { file: 'save' };

const determineMediaStatus = (uploadStatus, downloadStatus, blobUrl) => {
   if (downloadStatus === DOWNLOAD_STATUS.IN_PROGRESS) return mediaStatusConstants.DOWNLOADING;
   if (UPLOADING_STATUSES.has(uploadStatus)) return mediaStatusConstants.UPLOADING;
   if (uploadStatus === UPLOAD_STATUS.FAILED) return mediaStatusConstants.NOT_UPLOADED;

   const uploadDone = uploadStatus === UPLOAD_STATUS.COMPLETED;
   if (uploadDone && downloadStatus === DOWNLOAD_STATUS.COMPLETED) return mediaStatusConstants.LOADED;
   if (uploadDone && DOWNLOADABLE_STATUSES.has(downloadStatus) && !blobUrl) return mediaStatusConstants.NOT_DOWNLOADED;

   return mediaStatusConstants.UNKNOWN;
};

/**
 * Custom hook for managing media upload/download progress.
 *
 * @param {Object}  message
 * @param {boolean} [options.autoDownload=false]
 * @param {boolean} [options.fileSave=false]
 */
export const useMediaProgress = (message, { autoDownload = false, fileSave = false } = {}) => {
   const dispatch = useDispatch();

   const { msgId, msgBody = {}, msgType } = message;
   const {
      is_uploading: uploadStatus = 0,
      is_downloaded: downloadStatus = 0,
      blobUrl,
      file_size: fileSize = 0,
   } = msgBody.media ?? {};
   const chatId = getUserIdFromJid(useActiveRightStackJid());
   const isOnline = useIsOnline();

   const progressEntry = useSelector(state => state.mediaProgressData[msgId] ?? null);
   const targetMessage = useSelector(
      state => state.conversationData.data[chatId]?.messages?.find(msg => msg.msgId === msgId) ?? null,
   );

   const mediaStatus = useMemo(
      () => determineMediaStatus(uploadStatus, downloadStatus, blobUrl),
      [uploadStatus, downloadStatus, blobUrl],
   );

   const { progress, totalFileSize } = useMemo(() => {
      let loadedBytes = 0;

      if (mediaStatus === mediaStatusConstants.LOADED) {
         loadedBytes = fileSize;
      } else if (mediaStatus === mediaStatusConstants.DOWNLOADING || mediaStatus === mediaStatusConstants.UPLOADING) {
         if (progressEntry) {
            if (mediaStatus === mediaStatusConstants.DOWNLOADING && progressEntry.downloadedBytes != null) {
               loadedBytes = Math.min(progressEntry.downloadedBytes, fileSize);
            } else if (mediaStatus === mediaStatusConstants.UPLOADING && progressEntry.uploadedBytes != null) {
               loadedBytes = Math.min(progressEntry.uploadedBytes, fileSize);
            } else if (progressEntry.progress != null) {
               loadedBytes = (progressEntry.progress / 100) * fileSize;
            }
         }
      }

      return {
         progress: fileSize > 0 ? Math.round((loadedBytes / fileSize) * 100) : 0,
         totalFileSize: formatBytes(fileSize, 0),
      };
   }, [mediaStatus, progressEntry, fileSize]);

   const isProgressing =
      mediaStatus === mediaStatusConstants.UPLOADING || mediaStatus === mediaStatusConstants.DOWNLOADING;
   const isRetryable = mediaStatus === mediaStatusConstants.NOT_UPLOADED;

   // For auto-download effect — only fires when genuinely NOT_DOWNLOADED
   const isDownloadable = mediaStatus === mediaStatusConstants.NOT_DOWNLOADED && !isProgressing;

   // For UI button — file types always show download button even after loaded
   const isDownloadButtonVisible =
      (mediaStatus === mediaStatusConstants.NOT_DOWNLOADED || PERSISTENT_DOWNLOAD_TYPES.has(msgType)) &&
      !isProgressing &&
      !isRetryable;

   const updateStatus = useCallback(
      (status, extra = {}) =>
         dispatch(
            updateMediaStatus({
               chatId,
               msgId,
               is_uploading: UPLOAD_STATUS.COMPLETED,
               is_downloaded: status,
               ...extra,
            }),
         ),
      [dispatch, chatId, msgId],
   );

   const downloadMedia = useCallback(async () => {
      const action = MEDIA_ACTION_MAP[msgType] ?? 'download';
      const isSaveAction = action === 'save';
      if (!targetMessage) return;

      if (!isSaveAction) updateStatus(DOWNLOAD_STATUS.IN_PROGRESS);

      try {
         const result = await handleMediaAction(targetMessage, action, fileSave);
         const succeeded = result?.statusCode === 200;

         // For save action — status already set inside resolveBlob, don't overwrite
         // For download action — only set blobUrl if it actually exists
         if (!isSaveAction) {
            updateStatus(
               succeeded ? DOWNLOAD_STATUS.COMPLETED : DOWNLOAD_STATUS.FAILED,
               succeeded && result?.blobUrl ? { blobUrl: result.blobUrl } : {},
            );
         }
      } catch (err) {
         if (!isSaveAction) updateStatus(DOWNLOAD_STATUS.FAILED);
         logger.error('[useMediaProgress] Download error:', err);
      }
   }, [targetMessage, msgType, fileSave, updateStatus]);

   const cancelProgress = useCallback(async () => {
      if (!targetMessage) return;

      const isUploading = UPLOADING_STATUSES.has(uploadStatus);
      const isDownloading = downloadStatus === DOWNLOAD_STATUS.IN_PROGRESS;

      if (!isUploading && !isDownloading) return;

      try {
         if (isUploading) await handleMediaAction(targetMessage, 'cancelUpload');
         if (isDownloading) await handleMediaAction(targetMessage, 'cancelDownload');
      } catch (err) {
         logger.error('[useMediaProgress] Cancel error:', err);
      }
   }, [targetMessage, uploadStatus, downloadStatus]);

   const retryUploadMedia = useCallback(() => {
      if (isOnline && targetMessage) handleMediaAction(targetMessage, 'resumeUpload');
   }, [isOnline, targetMessage]);

   // Auto-download — msgId in deps ensures fresh start per message, no stale closure
   useEffect(() => {
      if (!autoDownload || !isDownloadable) return;
      if (downloadStatus !== DOWNLOAD_STATUS.NOT_STARTED) return;
      if (!isOnline) return;
      downloadMedia();
   }, [autoDownload, isDownloadable, downloadStatus, isOnline, downloadMedia]);

   return {
      mediaStatus,
      progress,
      totalFileSize,
      isProgressing,
      isRetryable,
      isDownloadable: isDownloadButtonVisible,
      downloadMedia,
      cancelProgress,
      retryUploadMedia,
   };
};

export default useMediaProgress;
